import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { RadioButton, RadioGroup } from "src/components/radio";
import { FilterTitle } from "src/features/filters/components/modal/filter-title";
import { match } from "ts-pattern";
import { z } from "zod";
import type { FilterModalProps } from "../../components/modal/filter-modal";
import { FilterModal } from "../../components/modal/filter-modal";
import { FilterModalButtons } from "../../components/modal/modal-buttons";
import { FilterModalError } from "../../components/modal/modal-error";
import { useFilterForm } from "../../components/modal/use-filter-form";
import type { FilterFormProps } from "../modals/types";
import { useDispatchFilter } from "../modals/use-dispatch-filter";
import type { LocalMaterialFilter } from "../types";

const createDefaultValues = (
  initialValue?: NonNullable<{
    withCoordinates: LocalMaterialFilter["withCoordinates"];
    withoutCoordinates: LocalMaterialFilter["withoutCoordinates"];
  }>,
) => {
  const defaultValues = {
    isPositioned: initialValue?.withCoordinates === true,
    isNotPositioned: initialValue?.withoutCoordinates === true,
  };

  return {
    defaultValues,
    initialOperation: defaultValues.isPositioned
      ? "is"
      : defaultValues.isNotPositioned
        ? "isNot"
        : "is",
  };
};

const resolver = zodResolver(
  z.object({
    isPositioned: z.boolean(),
    isNotPositioned: z.boolean(),
  }),
);

export const PositionFilterForm = ({
  initialValue,
  onClose,
  dispatchFilters,
}: FilterFormProps) => {
  const { error, operation, handleRadioChange, formMethods } = useFilterForm({
    resolver,
    ...createDefaultValues({
      withCoordinates: initialValue?.withCoordinates,
      withoutCoordinates: initialValue?.withoutCoordinates,
    }),
  });

  const { addModifyFilter, deleteFilter } = useDispatchFilter({
    dispatchFilters,
    id: initialValue?.id,
    onClose,
  });

  const onSubmit = useMemo(
    () =>
      formMethods.handleSubmit(() => {
        match(operation)
          .with("is", () => {
            addModifyFilter({
              withCoordinates: true,
            });
            onClose();
          })
          .with("isNot", () => {
            addModifyFilter({
              withoutCoordinates: true,
            });
            onClose();
          })
          .run();
      }),
    [formMethods, operation, onClose, addModifyFilter],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit}>
        <div className="mb-4 flex flex-col gap-4">
          <FilterTitle title="Position" deleteFilter={deleteFilter} />
          <FilterModalError error={error} />
          <RadioGroup
            value={operation}
            onChange={handleRadioChange}
            label="Operation"
          >
            {(props) => (
              <div className="flex flex-col gap-5">
                <RadioButton
                  label="Is positioned"
                  selectedValue="is"
                  testId="material-is-positioned-radio"
                  {...props}
                />
                <RadioButton
                  label="Is not positioned"
                  selectedValue="isNot"
                  testId="material-is-not-positioned-radio"
                  {...props}
                />
              </div>
            )}
          </RadioGroup>
        </div>
        <FilterModalButtons onCancel={onClose} testIdPrefix="position" />
      </form>
    </FormProvider>
  );
};

// Seperated from form component to make sure popover unmount causes state reset
export const PositionFilterModal = ({
  open,
  onClose,
  anchorEl,
  ...props
}: FilterFormProps & FilterModalProps) => (
  <FilterModal anchorEl={anchorEl} open={open} onClose={onClose}>
    <PositionFilterForm onClose={onClose} {...props} />
  </FilterModal>
);
